/**
 * VistoeraTASK-00567 — which failures may end a session on the client.
 *
 * Only the auth service saying the session is missing/invalid (401 with an
 * auth code) logs the user out. Timeouts, aborted/network failures, 5xx
 * (including 503 `auth_service_unavailable`) and permission 403s mean "could
 * not check", never "not logged in".
 */
import type { SaasApiError } from './api'

const SESSION_REJECTED_CODES = new Set(['not_authenticated', 'invalid_session', 'AUTH_ERROR'])

export function isDefinitiveUnauthenticated(err: unknown): boolean {
  const e = err as SaasApiError | undefined
  if (e?.status !== 401) return false
  return !e.code || SESSION_REJECTED_CODES.has(e.code)
}

/** No HTTP answer at all (timeout / network / CORS-masked edge error) or a
 * 5xx: the request may simply have hit a deploy/restart window. */
export function isTransientFailure(err: unknown): boolean {
  const status = (err as SaasApiError | undefined)?.status
  return status === undefined || status >= 500
}

export const TRANSIENT_RETRY_DELAY_MS = 800

/** One quiet retry for user-initiated auth actions (account switch, account
 * list) so a single deploy-window blip is not shown as a failure. 4xx are
 * never retried. */
export async function retryTransientOnce<T>(
  fetcher: () => Promise<T>,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<T> {
  try {
    return await fetcher()
  } catch (err) {
    if (!isTransientFailure(err)) throw err
    await sleep(TRANSIENT_RETRY_DELAY_MS)
    return fetcher()
  }
}

/** Delays between boot `/me` attempts when the answer is not definitive. */
export const AUTH_BOOT_RETRY_DELAYS_MS: readonly number[] = [1000, 3000]

export async function fetchWithBootRetry<T>(
  fetcher: () => Promise<T>,
  delaysMs: readonly number[] = AUTH_BOOT_RETRY_DELAYS_MS,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetcher()
    } catch (err) {
      if (isDefinitiveUnauthenticated(err) || attempt >= delaysMs.length) throw err
      await sleep(delaysMs[attempt])
    }
  }
}
